import React, { useState } from 'react';
import styled from 'styled-components';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Text } from '../components';
import { Button } from '../styles';
import { capitializeString, replaceUnderscore } from '../../../assets/js/utils';
import { startCase } from 'lodash';

const EditHeader = styled(Flex)`
  justify-content: space-between;
  margin: 6px 6px 0 0;
`;

const Container = styled.div`
    background-color: #F9FAFB;
    border-radius: 4px;
    padding: 20px;
    border: 1px #D4D4D8 solid;
`;
const StyledText = styled(Text)`
    font-weight: ${(props) => props.fontWeight || 600};
    font-size: ${(props) => props.fontSize || '14px'};
    font-style: ${(props) => props.fontStyle || 'normal'};
    color: ${(props) => props.color || '#27272A'};
`;
const InformationBanner = styled(Flex)`
    background: #F3FAFF;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
`;

const StyledInfoIcon = styled(InfoCircleOutlined)`
    color: #3FABF8;
    font-size: 18px;
    margin-right: 13px;
    padding-top: 4px;
`;
const renderLines = (items) => (
  <Flex
    flexDirection="column"
  >
    {
                items.map((item) => (
                  <Flex style={{ marginBottom: '12px' }}>
                    <Text bold style={{ flex: 2 }}>
                      {item.label}
                    </Text>
                    <Text style={{ flex: 3 }}>
                      {item.value}
                    </Text>
                  </Flex>
                ))
            }
  </Flex>
);

const NoInput = <StyledText> - </StyledText>;

// helper function: to combine full name
export const formatFullName = (firstName = '', middleInitial = '', lastName = '') => `${firstName} ${middleInitial}. ${lastName}`;

// helper function: to combine full address
export const formatFullAddress = (addressline1 = '', city = '', state = '', zip = '', country = '') => {
  const result = `${addressline1} ${city} ${state} ${zip} ${country}`;
  if (result.trim().length === 0) {
    return '';
  }

  return result;
};

// takes in the key and questions array
const extractAnswersHelper = (key, questions) => {
  const question = questions.find((q) => q.key === key);
  if (!question) return undefined;
  return question.answer.answer[key];
};

function ReviewStep(props) {
  const [acknowledgementStatus, SetAcknowledgementStatus] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const {
    questions, onReviewStateHandler, onSubmitHandler, renderSubmitText, emergencyContactConsent,
  } = props;
  const [firstName, middleInitial, lastName] = [extractAnswersHelper('first_name', questions), extractAnswersHelper('middle_initial', questions), extractAnswersHelper('last_name', questions)];
  const line1Ans = extractAnswersHelper('home_address_line1', questions)
    || extractAnswersHelper('address_line_1', questions);
  const line2Ans = extractAnswersHelper('home_address_line2', questions)
    || extractAnswersHelper('address_line_2', questions);
  const legacyStreet = extractAnswersHelper('address_address', questions);
  const streetCombined = [line1Ans, line2Ans].filter((s) => s != null && String(s).trim() !== '').join(', ')
    || legacyStreet;
  const [addressline1, city, state, zip, country] = [
    streetCombined,
    extractAnswersHelper('address_city', questions),
    extractAnswersHelper('address_state', questions),
    extractAnswersHelper('address_zipcode', questions),
    extractAnswersHelper('address_country', questions),
  ];
  const infoList = [
    { label: 'Name', value: formatFullName(firstName, middleInitial, lastName) || NoInput },
    { label: 'Preferred Name', value: extractAnswersHelper('preferred_name', questions) || NoInput },
    { label: 'Phone', value: extractAnswersHelper('phone', questions) || NoInput },
    { label: 'Email', value: extractAnswersHelper('email', questions) || NoInput },
    { label: 'Date of Birth', value: extractAnswersHelper('birthday', questions) || NoInput },
    { label: 'Ethnicity', value: replaceUnderscore(capitializeString(extractAnswersHelper('ethnicity', questions))) || NoInput },
    { label: 'Gender', value: capitializeString(extractAnswersHelper('gender', questions)) || NoInput },
    { label: 'Marital Status', value: capitializeString(extractAnswersHelper('marital_status', questions)) || NoInput },
    { label: 'Social Security Number (SSN)', value: extractAnswersHelper('ssn', questions) || NoInput },
    { label: 'Home Address', value: formatFullAddress(addressline1, city, state, zip, country) || NoInput },
  ];
  const contactList = [
    { label: 'Contact name', value: extractAnswersHelper('emergency_contact_name', questions) || NoInput },
    { label: 'Phone', value: extractAnswersHelper('emergency_contact_phone', questions) || NoInput },
    { label: 'Email', value: extractAnswersHelper('emergency_contact_email', questions) || NoInput },
    { label: 'Relationship with contact', value: startCase(extractAnswersHelper('emergency_contact_relationship', questions)) || NoInput },
    { label: 'Consent to notify emergency contact', value: emergencyContactConsent ? 'Yes' : 'No' },
  ];

  const handleEmployeeClickSubmit = () => {
    // reset
    setErrorMsg('');
    if (!acknowledgementStatus) {
      setErrorMsg('Required before submitting');
    } else {
      onSubmitHandler();
    }
  };

  return (
    <div>
      <StyledText fontWeight="700" fontSize="36px" color="#787883" marginBottom="30px">
        Review
      </StyledText>
      {/* information banner */}
      <InformationBanner>
        <StyledInfoIcon />
        <StyledText fontWeight="400">
          <b>This information will be used to prepare your new job's onboarding process.</b>
          {' '}
          Please ensure your information is correct before submitting.
        </StyledText>
      </InformationBanner>
      {/* review content */}
      <Container>
        <div>
          <EditHeader>
            <StyledText fontSize="16px" fontWeight="700" marginBottom={16}>
              Personal information
            </StyledText>

            <a onClick={onReviewStateHandler} style={{ color: '#3F51B5', cursor: 'pointer', fontWeight: '600' }}>
              Edit
            </a>
          </EditHeader>
          { renderLines(infoList) }
        </div>

        <div>
          <StyledText fontSize="16px" fontWeight="700" marginBottom={16}>
            Emergency Contact Information
          </StyledText>
          { renderLines(contactList) }
        </div>
      </Container>
      {/* acknowledgement checkbox */}
      <Flex marginTop="20px" marginBottom="5px">
        <input
          style={{ marginRight: '8px', width: '20px' }}
          name="acknowledgementCheckbox"
          data-testid="basic-info-acknowledgement-checkbox"
          type="checkbox"
          id="acknowledgement"
          checked={acknowledgementStatus}
          onChange={() => {
            setErrorMsg('');
            SetAcknowledgementStatus(!acknowledgementStatus);
          }}
        />
        <Text>
          <label htmlFor="acknowledgement" style={{ cursor: 'pointer' }}>
            I acknowledge that the information above is correct
          </label>
        </Text>
      </Flex>
      <StyledText color="#FF7152" fontSize="14px" fontWeight="400">
        { errorMsg }
      </StyledText>
      {/* submit button */}
      <Flex justifyContent="flex-end">
        <Button onClick={handleEmployeeClickSubmit} loading={props.loading} data-testid="basic-info-submit">
          {renderSubmitText()}
        </Button>
      </Flex>
    </div>
  );
}

export default ReviewStep;
